import supabase from './supabaseClient'

// 配置：存储桶与内容路径前缀
const CONTENT_BUCKET = 'articles'
const CONTENT_PREFIX = 'content'

function generateSlug(title) {
    return (title || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9\-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
}

function buildContentPath(title, id) {
    const slug = generateSlug(title) || 'article'
    const ts = Date.now()
    const safeId = id || 'new'
    return `${CONTENT_PREFIX}/${safeId}_${ts}_${slug}.md`
}

async function uploadContentToStorage(path, content) {
    // 使用 upsert 便于更新
    const { error } = await supabase.storage
        .from(CONTENT_BUCKET)
        .upload(path, new Blob([content], { type: 'text/markdown' }), { upsert: true })
    if (error) throw new Error(error.message)
    return path
}

function getPublicUrl(path) {
    const { data } = supabase.storage.from(CONTENT_BUCKET).getPublicUrl(path)
    return data?.publicUrl || null
}

async function loadContent(pathOrUrl) {
    let url = pathOrUrl

    // 如果是路径，转换为公共URL
    if (!pathOrUrl.startsWith('http')) {
        const publicUrl = getPublicUrl(pathOrUrl)
        if (!publicUrl) return null
        url = publicUrl
    }

    // 获取内容
    const res = await fetch(url)
    if (!res.ok) return null
    return await res.text()
}

async function setArticleTags(articleId, tagIds) {
    // 清空并重建（可优化为 diff）
    await supabase.from('article_tags').delete().eq('article_id', articleId)
    if (tagIds && tagIds.length > 0) {
        const rows = tagIds.map(id => ({ article_id: articleId, tag_id: id }))
        const { error } = await supabase.from('article_tags').insert(rows)
        if (error) throw new Error(error.message)
    }
}

function mapSummaryToExcerpt(row) {
    if (!row) return row
    const { summary, ...rest } = row
    return { ...rest, excerpt: summary }
}

function computeCharCount(text) {
    if (!text) return 0
    return text.replace(/\s/g, '').length
}

class SupabaseArticleService {
    // 前台：公开发布的文章列表
    async listPublished({ limit, offset = 0, categoryId, tagId } = {}) {
        let query = supabase.from('articles')
            .select('id, title, summary, cover_image_url, category_id, status, visibility, word_count, author_id, published_at, updated_at, article_type')
            .eq('status', 'published')
            .eq('visibility', 'public')
            .order('published_at', { ascending: false })

        if (categoryId) query = query.eq('category_id', categoryId)

        if (typeof limit === 'number') query = query.range(offset, offset + limit - 1)

        // 标签筛选需联表
        if (tagId) {
            const { data: ids, error: tagErr } = await supabase
                .from('article_tags')
                .select('article_id')
                .eq('tag_id', tagId)
            if (tagErr) throw new Error(tagErr.message)
            const idList = (ids || []).map(r => r.article_id)
            if (idList.length === 0) return []
            const { data, error } = await query.in('id', idList)
            if (error) throw new Error(error.message)
            return (data || []).map(mapSummaryToExcerpt)
        }

        const { data, error } = await query
        if (error) throw new Error(error.message)
        return (data || []).map(mapSummaryToExcerpt)
    }

    async listPublishedByCursor({ limit = 20, cursor = null, categoryId, tagId, tagIds } = {}) {
        let query = supabase
            .from('articles')
            .select('id, title, summary, cover_image_url, category_id, status, visibility, word_count, author_id, published_at, updated_at, article_type')
            .eq('status', 'published')
            .eq('visibility', 'public')
            .order('updated_at', { ascending: false })
            .order('id', { ascending: false })

        if (categoryId) query = query.eq('category_id', categoryId)

        if (cursor && cursor.updated_at && cursor.id) {
            const updatedAtISO = new Date(cursor.updated_at).toISOString()
            // keyset: (updated_at, id) < (cursor.updated_at, cursor.id)
            query = query.or(`and(updated_at.eq.${updatedAtISO},id.lt.${cursor.id}),updated_at.lt.${updatedAtISO}`)
        }

        // 兼容单标签筛选（向后兼容）
        if (tagId) {
            const { data: ids, error: tagErr } = await supabase
                .from('article_tags')
                .select('article_id')
                .eq('tag_id', tagId)
            if (tagErr) throw new Error(tagErr.message)
            const idList = (ids || []).map(r => r.article_id)
            if (idList.length === 0) return { items: [], nextCursor: null }
            query = query.in('id', idList)
        }

        // 多标签筛选（AND逻辑：文章必须包含所有选中的标签）
        if (tagIds && tagIds.length > 0) {
            for (const selectedTagId of tagIds) {
                const { data: ids, error: tagErr } = await supabase
                    .from('article_tags')
                    .select('article_id')
                    .eq('tag_id', selectedTagId)
                if (tagErr) throw new Error(tagErr.message)
                const idList = (ids || []).map(r => r.article_id)
                if (idList.length === 0) return { items: [], nextCursor: null }
                query = query.in('id', idList)
            }
        }

        const { data, error } = await query.limit(limit)
        if (error) throw new Error(error.message)
        const items = (data || []).map(mapSummaryToExcerpt)
        const next = items.length === limit ? { updated_at: items[items.length - 1].updated_at, id: items[items.length - 1].id } : null
        return { items, nextCursor: next }
    }

    // 管理端：全部文章列表
    async listAll() {
        const { data, error } = await supabase
            .from('articles')
            .select('id, title, summary, cover_image_url, category_id, status, visibility, word_count, author_id, published_at, updated_at, created_at, article_type')
            .order('updated_at', { ascending: false })
        if (error) throw new Error(error.message)
        return (data || []).map(mapSummaryToExcerpt)
    }

    // 前台：获取公开文章详情（含内容）
    async getPublicById(id) {
        const { data: article, error } = await supabase
            .from('articles')
            .select('id, title, summary, cover_image_url, category_id, status, visibility, content, content_url, word_count, author_id, published_at, updated_at, article_type')
            .eq('id', id)
            .eq('status', 'published')
            .eq('visibility', 'public')
            .single()
        if (error) return null
        const content = article.content || (article.content_url ? await loadContent(article.content_url) : null)
        return { ...mapSummaryToExcerpt(article), content }
    }

    // 管理端：获取文章详情（含内容与 tag_ids）
    async getByIdForAdmin(id) {
        const { data: article, error } = await supabase
            .from('articles')
            .select('id, title, summary, cover_image_url, category_id, status, visibility, content, content_url, word_count, author_id, published_at, updated_at, article_type')
            .eq('id', id)
            .single()
        if (error) return null

        const { data: tags, error: tagErr } = await supabase
            .from('article_tags')
            .select('tag_id')
            .eq('article_id', id)
        if (tagErr) throw new Error(tagErr.message)

        const content = article.content || (article.content_url ? await loadContent(article.content_url) : '')
        return { ...mapSummaryToExcerpt(article), tag_ids: (tags || []).map(t => t.tag_id), content }
    }

    // 创建文章（草稿或发布）
    async create({ title, excerpt, content, cover_image_url, category_id, tag_ids = [], status = 'draft', visibility = 'public', article_type = 'markdown' }) {
        const { data: { user } = {}, error: userErr } = await supabase.auth.getUser()
        if (userErr) throw new Error(userErr.message)

        const hasContentUrl = !!arguments[0]?.content_url
        // 修复：对象存储模式下，需要从原始内容或content_url加载内容来计算字数
        let contentForWordCount = content
        if (hasContentUrl && !content && arguments[0]?.content_url) {
            // 如果是对象存储模式且没有直接内容，尝试从URL加载内容计算字数
            try {
                contentForWordCount = await loadContent(arguments[0].content_url)
            } catch (error) {
                console.warn('无法从content_url加载内容用于字数统计:', error)
                contentForWordCount = ''
            }
        }
        // 如果传递了原始内容用于字数统计，优先使用
        if (arguments[0]?.originalContent) {
            contentForWordCount = arguments[0].originalContent
        }
        const wordCount = computeCharCount(contentForWordCount)

        // 优先使用用户手动提供的 content_url；否则保存内嵌内容
        const payload = {
            title,
            summary: excerpt || (contentForWordCount ? (contentForWordCount.replace(/\s+/g, ' ').slice(0, 60)) : ''),
            cover_image_url: cover_image_url || null,
            category_id: category_id || null,
            status,
            visibility,
            content: hasContentUrl ? null : (content && content.trim() ? content : null),
            content_url: hasContentUrl ? arguments[0].content_url : null,
            word_count: wordCount,
            author_id: user?.id || null,
            published_at: status === 'published' ? new Date().toISOString() : null,
            article_type: article_type || 'markdown'
        }

        console.log('🔍 SupabaseArticleService.create - 调试信息:')
        console.log('🔍 hasContentUrl:', hasContentUrl)
        console.log('🔍 contentForWordCount长度:', contentForWordCount?.length || 0)
        console.log('🔍 计算得到的字数:', wordCount)
        console.log('🔍 传入的参数:', JSON.stringify(arguments[0], null, 2))
        console.log('🔍 最终payload:', JSON.stringify(payload, null, 2))

        const { data: created, error } = await supabase
            .from('articles')
            .insert(payload)
            .select('id')
            .single()
        if (error) throw new Error(error.message)

        if (tag_ids.length > 0) await setArticleTags(created.id, tag_ids)

        return await this.getByIdForAdmin(created.id)
    }

    // 更新文章
    async update(id, { title, excerpt, content, cover_image_url, category_id, tag_ids = [], status, visibility, article_type }) {
        const { data: { user } = {}, error: userErr } = await supabase.auth.getUser()
        if (userErr) throw new Error(userErr.message)

        const hasContentUrl = !!arguments[1]?.content_url
        // 修复：对象存储模式下，需要从原始内容或content_url加载内容来计算字数
        let contentForWordCount = content
        if (hasContentUrl && !content && arguments[1]?.content_url) {
            // 如果是对象存储模式且没有直接内容，尝试从URL加载内容计算字数
            try {
                contentForWordCount = await loadContent(arguments[1].content_url)
            } catch (error) {
                console.warn('无法从content_url加载内容用于字数统计:', error)
                contentForWordCount = ''
            }
        }
        // 如果传递了原始内容用于字数统计，优先使用
        if (arguments[1]?.originalContent) {
            contentForWordCount = arguments[1].originalContent
        }
        const wordCount = computeCharCount(contentForWordCount)

        const payload = {
            title,
            summary: excerpt || (contentForWordCount ? (contentForWordCount.replace(/\s+/g, ' ').slice(0, 60)) : ''),
            cover_image_url: cover_image_url || null,
            category_id: category_id || null,
            status,
            visibility,
            content: hasContentUrl ? null : (content && content.trim() ? content : null),
            content_url: hasContentUrl ? arguments[1].content_url : null,
            word_count: wordCount,
            updated_at: new Date().toISOString()
        }

        // 如果提供了 article_type，则更新
        if (article_type !== undefined) {
            payload.article_type = article_type
        }

        // 如果状态变为 published，设置 published_at
        if (status === 'published') {
            payload.published_at = new Date().toISOString()
        }

        const { error } = await supabase
            .from('articles')
            .update(payload)
            .eq('id', id)
        if (error) throw new Error(error.message)

        await setArticleTags(id, tag_ids)

        return await this.getByIdForAdmin(id)
    }

    // 逻辑删除（软删除）：将状态设置为deleted
    async softDelete(id) {
        const { error } = await supabase
            .from('articles')
            .update({
                status: 'deleted',
                deleted_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
        if (error) throw new Error(error.message)
        return await this.getByIdForAdmin(id)
    }

    // 永久删除（物理删除）
    async permanentDelete(id) {
        // 删除关联
        await supabase.from('article_tags').delete().eq('article_id', id)
        const { error } = await supabase.from('articles').delete().eq('id', id)
        if (error) throw new Error(error.message)
        return true
    }

    // 恢复已删除的文章
    async restore(id, newStatus = 'draft') {
        const { error } = await supabase
            .from('articles')
            .update({
                status: newStatus,
                deleted_at: null,
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
        if (error) throw new Error(error.message)
        return await this.getByIdForAdmin(id)
    }

    // 保持原有的remove方法作为向后兼容，默认使用逻辑删除
    async remove(id) {
        return await this.softDelete(id)
    }

    async toggleVisibility(id) {
        const { data, error } = await supabase
            .from('articles')
            .select('visibility')
            .eq('id', id)
            .single()
        if (error) throw new Error(error.message)
        const newVisibility = data.visibility === 'public' ? 'private' : 'public'
        const { error: upErr } = await supabase
            .from('articles')
            .update({ visibility: newVisibility })
            .eq('id', id)
        if (upErr) throw new Error(upErr.message)
        return await this.getByIdForAdmin(id)
    }

    async updateStatus(id, newStatus) {
        if (!['draft', 'published', 'deleted'].includes(newStatus)) {
            throw new Error('invalid status')
        }
        const payload = { status: newStatus }
        if (newStatus === 'published') payload.published_at = new Date().toISOString()
        if (newStatus === 'draft' || newStatus === 'deleted') payload.published_at = null

        const { error } = await supabase
            .from('articles')
            .update(payload)
            .eq('id', id)
        if (error) throw new Error(error.message)
        return await this.getByIdForAdmin(id)
    }

    // 获取统计信息
    async getStats() {
        try {
            // 获取已发布文章数量
            const { count: articleCount, error: articleError } = await supabase
                .from('articles')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'published')
                .eq('visibility', 'public')

            if (articleError) throw articleError

            // 获取总字数
            const { data: wordCountData, error: wordCountError } = await supabase
                .from('articles')
                .select('word_count')
                .eq('status', 'published')
                .eq('visibility', 'public')

            if (wordCountError) throw wordCountError

            const totalWords = wordCountData.reduce((sum, article) => sum + (article.word_count || 0), 0)

            return {
                articleCount: articleCount || 0,
                totalWords: totalWords
            }
        } catch (error) {
            console.error('获取文章统计失败:', error)
            throw new Error(error.message)
        }
    }
}

const supabaseArticleService = new SupabaseArticleService()
export default supabaseArticleService 